import { CalculationMethod } from "view/calculationmethods/types/calculationmethod";
import { Fragment, ReactNode } from "react";
import { Session } from "ezStore";
import { AgreementTypeEnum } from "__generated__/graphql";
import { useSchema } from "@schemas/calculationmethods/useSchema";
import { useSchema as useAgreementSchema } from "@schemas/agreements/useSchema";
import { CalculationMethodDetailsByTypeProps, RegistryComponent } from "@schemas/calculationmethods/schema";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

type CalculationMethodDetailsProps = {
	agreementType: AgreementTypeEnum;
	calculationMethod: CalculationMethod;
	currency: CurrencyFragment;
	session: Session;
}

type DetailsFieldProps = {
	calculationMethod: CalculationMethod;
	session: Session;
	currency: CurrencyFragment;
	CalculationMethodSchemaDetails?: RegistryComponent<CalculationMethodDetailsByTypeProps>;
	agreementType: AgreementTypeEnum;
	hiddenFields?: string[];
}

type DetailsField = {
	id: string;
	render: (props: DetailsFieldProps) => ReactNode;
}

const CalculationMethodDetailsFields: DetailsField[] = [
	{
		id: "calculationMethod",
		render: ({ calculationMethod, currency, session, CalculationMethodSchemaDetails, agreementType, hiddenFields }) => CalculationMethodSchemaDetails && (
			<CalculationMethodSchemaDetails
				hiddenFields={hiddenFields}
				calculationMethod={calculationMethod}
				currency={currency}
				session={session}
				agreementType={agreementType}
			/>
		),
	},
];

const CalculationMethodDetails = ({ agreementType, currency, session, calculationMethod }: CalculationMethodDetailsProps) => {
	const agreementSchema = useAgreementSchema(agreementType);
	const schema = useSchema(calculationMethod.type);

	const fields = {
		...(agreementSchema?.calculationMethodFields ?? {}),
		...(schema?.fields ?? {}),
	};

	const hiddenFields = Object.entries(fields ?? {}).filter(([ , x ]) => {
		const rules = x.rules;
		return rules?.hidden || rules?.hiddenIf?.({ calculationMethodType: calculationMethod.type });
	}).map(([ id ]) => id);

	return (
		<>
			{CalculationMethodDetailsFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render({
							calculationMethod,
							session,
							currency,
							CalculationMethodSchemaDetails: schema?.Details,
							agreementType,
							hiddenFields,
						})}
					</Fragment>
				))
			}
		</>
	);
};

export default CalculationMethodDetails;
